import React from 'react';

import { useUIColors } from '../../theme';

export interface ColumnResizeHandleProps {
    /** Column this grip resizes. */
    columnKey: string;
    /** From `useResizableColumns`. */
    startResize: (key: string, event: React.PointerEvent) => void;
    /** From `useResizableColumns`. Arrow keys move by `step`, Shift by 10×. */
    nudge: (key: string, deltaPx: number) => void;
    /** True while this column is the one being dragged. */
    active?: boolean;
    /** Accessible name, e.g. "Đổi độ rộng cột Mã quyền". */
    label: string;
    /** px per arrow press. Default 16. */
    step?: number;
    /** Double-click restores the default width. */
    onReset?: () => void;
    className?: string;
}

/**
 * The grip on a column's trailing edge.
 *
 * `role="separator"` with `aria-orientation="vertical"` and arrow-key handling,
 * because a resize you can only reach by dragging is a resize half the users
 * cannot perform. The visual is a hit area wider than the line it draws — a 1px
 * target is a miss on the first try for most people, and on this table a miss
 * range-selects the header text instead.
 *
 * Positioned `absolute`, so the header cell it sits in must be `position:
 * relative`. It carries only geometry inline; colour comes from the
 * `useUIColors()` seam and any further skin from `className`.
 */
export function ColumnResizeHandle({
    columnKey,
    startResize,
    nudge,
    active = false,
    label,
    step = 16,
    onReset,
    className,
}: ColumnResizeHandleProps): React.ReactElement {
    const colors = useUIColors();

    function handleKeyDown(event: React.KeyboardEvent) {
        const big = event.shiftKey ? 10 : 1;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            nudge(columnKey, -step * big);
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            nudge(columnKey, step * big);
        } else if ((event.key === 'Enter' || event.key === ' ') && onReset) {
            event.preventDefault();
            onReset();
        }
    }

    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={label}
            tabIndex={0}
            className={className}
            onPointerDown={(e) => startResize(columnKey, e)}
            onDoubleClick={onReset}
            onKeyDown={handleKeyDown}
            style={{
                position: 'absolute',
                top: 0,
                // EAA-1839: was `right: -4`, overhanging 4-5px of this 9px hit box
                // PAST the column's own right border and into the NEXT header
                // `<th>`. Two adjacent `<th>` are both `position: relative` with no
                // `z-index`, so at equal stacking the LATER sibling paints on top —
                // measured in a real browser, every pointerdown in that overhung
                // strip (which is where the visible 2px line itself sat, being
                // centered in the box) landed on the next column's `<th>`, not this
                // separator, so `startResize` never fired and the drag silently did
                // nothing. `right: 0` keeps the full hit box inside the column being
                // resized, where it reliably wins hit-testing against its own
                // content instead of losing to a sibling.
                right: 0,
                bottom: 0,
                // 9px of hit area drawing a 2px line — see the docstring.
                width: 9,
                display: 'flex',
                justifyContent: 'center',
                cursor: 'col-resize',
                touchAction: 'none',
                zIndex: 1,
                // Idle grips would otherwise draw a second vertical rule beside
                // every real column border.
                opacity: active ? 1 : 0,
                transition: 'opacity 120ms ease',
            }}
            onPointerEnter={(e) => {
                e.currentTarget.style.opacity = '1';
            }}
            onPointerLeave={(e) => {
                if (!active) e.currentTarget.style.opacity = '0';
            }}
            onFocus={(e) => {
                e.currentTarget.style.opacity = '1';
            }}
            onBlur={(e) => {
                if (!active) e.currentTarget.style.opacity = '0';
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 2,
                    height: '100%',
                    borderRadius: 1,
                    background: colors.primary,
                }}
            />
        </span>
    );
}

export default ColumnResizeHandle;
